/**
 * Van sales: the salesman's screen, made for one hand on a phone in a shop
 * with no signal.
 *
 *   Home        today's work, the queue, the sync, and the three jobs:
 *   Sell        customer -> items (scan or search) -> cash or credit -> print
 *   Take back   goods a shop returns, credited or refunded
 *   Collect     cash against the customer's open bills
 *
 * Everything is saved on the phone first and sent when there is signal.
 * After a week without a sync the phone stops billing: prices and stock
 * that old are no longer worth trusting.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert, Box, Button, ButtonBase, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, InputAdornment, List, ListItemButton, MenuItem,
  Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import ScanIcon from '@mui/icons-material/QrCodeScanner'
import SyncIcon from '@mui/icons-material/Sync'
import SellIcon from '@mui/icons-material/PointOfSaleOutlined'
import ReturnIcon from '@mui/icons-material/AssignmentReturnOutlined'
import CollectIcon from '@mui/icons-material/PaymentsOutlined'
import PrintIcon from '@mui/icons-material/ReceiptLongOutlined'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { useTranslation } from 'react-i18next'

import { errorMessage } from '../api/client'
import { useAuth } from '../store/AuthContext'
import LoginPage from '../pages/Login'
import { isPositive, sanitize } from '../vouchers/money'
import { c, fmtMoney } from '../theme'
import { fmtQty } from '../inventory/qty'
import { registerPhone } from './api'
import DayIcon from '@mui/icons-material/EventAvailableOutlined'
import PrinterIcon from '@mui/icons-material/PrintOutlined'
import {
  daySummary, daysOffline, defaultUnit, factorOf, loadDocs, listPrice, minimumFor, priceLine, riyadhDate,
  totals, unitsOf, type DocKind, type DocLine, type VanCustomer, type VanDoc, type VanItem,
} from './model'
import { printDoc } from './print'
import { DEFAULT_PRINTER, loadPrinter, pair, printBlocks, printViaDialog, savePrinter,
         type PrinterSettings } from './printer'
import { bluetoothAvailable } from './bluetooth'
import { dayTicket, draw } from './ticket'
import { DOTS } from './escpos'
import { unloadVan } from './api'
import { VanCtx, useVan, useVanState } from './useVan'

type View = { name: 'home' } | { name: 'pick'; next: DocKind }
  | { name: 'sell'; kind: 'SALE' | 'RETURN'; customer: string | null }
  | { name: 'collect'; customer: string } | { name: 'docs' } | { name: 'printer' }
  | { name: 'day' }

const bal = (v: string) => (Number(v) < 0 ? `${fmtMoney(v.replace('-', ''))} Cr`
  : `${fmtMoney(v)}`)

export default function VanApp() {
  const van = useVan()
  return (
    <VanCtx.Provider value={van}>
      <Box sx={{ maxWidth: 560, mx: 'auto', minHeight: '100dvh', bgcolor: c.surfacePage,
                 pb: 'env(safe-area-inset-bottom)' }}>
        {van.device === undefined ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : van.device === null ? <Setup /> : <Main />}
      </Box>
    </VanCtx.Provider>
  )
}

// --- setting the phone up ----------------------------------------------------

function Setup() {
  const { t } = useTranslation()
  const { isAuthenticated, me, switchCompany } = useAuth()
  const { setUp } = useVanState()
  const [name, setName] = useState(() => guessDevice())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [takeover, setTakeover] = useState(false)

  if (!isAuthenticated) {
    return (
      <Box>
        <Alert severity="info" sx={{ m: 2 }}>{t('van.signInFirst')}</Alert>
        <LoginPage />
      </Box>
    )
  }
  async function go() {
    setBusy(true); setError(null)
    try {
      const r = await registerPhone({ device_name: name.trim() || 'Phone', takeover })
      await setUp(r.token, r.bootstrap)
    } catch (e) {
      const msg = errorMessage(e)
      setError(msg)
      if (/another phone/.test(msg)) setTakeover(true)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Stack spacing={2} sx={{ p: 2.5 }}>
      <Typography component="h1" sx={{ fontSize: 22, fontWeight: 600, m: 0 }}>
        {t('van.setupTitle')}</Typography>
      <Typography sx={{ color: c.inkMuted }}>
        {t('van.setupBody', { name: me?.full_name || me?.username })}</Typography>
      {(me?.companies?.length ?? 0) > 1 && (
        <TextField select label={t('auth.company')} value={me!.company_id}
          onChange={(e) => { setError(null); void switchCompany(Number(e.target.value)) }}>
          {me!.companies.map((co) => <MenuItem key={co.id} value={co.id}>{co.name_en}</MenuItem>)}
        </TextField>
      )}
      <TextField label={t('van.phoneName')} value={name} onChange={(e) => setName(e.target.value)}
        inputProps={{ maxLength: 80 }} />
      {error && <Alert severity="warning">{error}</Alert>}
      {takeover && <Alert severity="info">{t('van.takeoverHint')}</Alert>}
      <Button variant="contained" size="large" disabled={busy} onClick={() => void go()}>
        {busy ? <CircularProgress size={20} color="inherit" />
          : takeover ? t('van.takeOver') : t('van.setUp')}</Button>
    </Stack>
  )
}

function guessDevice(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const m = ua.match(/Android [\d.]+; ([^;)]+)/)
  return m ? m[1].replace(/ Build.*/, '').trim() : 'Phone'
}

// --- the app -----------------------------------------------------------------

function Main() {
  const [view, setView] = useState<View>({ name: 'home' })
  const home = () => setView({ name: 'home' })
  switch (view.name) {
    case 'pick':
      return <PickCustomer kind={view.next} onBack={home} onPick={(code) =>
        setView(view.next === 'RECEIPT' ? { name: 'collect', customer: code! }
          : { name: 'sell', kind: view.next as 'SALE' | 'RETURN', customer: code })} />
    case 'sell':
      return <Sell kind={view.kind} customer={view.customer} onDone={home} />
    case 'collect':
      return <Collect customer={view.customer} onDone={home} />
    case 'docs':
      return <Docs onBack={home} />
    case 'printer':
      return <PrinterScreen onBack={home} />
    case 'day':
      return <EndOfDay onBack={home} />
    default:
      return <Home go={setView} />
  }
}

function TopBar({ title, onBack, right }: { title: ReactNode; onBack?: () => void
                                            right?: ReactNode }) {
  const { t } = useTranslation()
  return (
    <Stack direction="row" alignItems="center" spacing={1}
      sx={{ position: 'sticky', top: 0, zIndex: 2, px: 1.5, py: 1, bgcolor: c.surface,
            borderBottom: `1px solid ${c.border}`, minHeight: 56 }}>
      {onBack && <IconButton aria-label={t('van.back')} onClick={onBack}><ArrowBackIcon /></IconButton>}
      <Box sx={{ flex: 1, minWidth: 0 }}>{title}</Box>
      {right}
    </Stack>
  )
}

function SyncButton() {
  const { t } = useTranslation()
  const { online, syncing, sync, docs } = useVanState()
  const waiting = docs.filter((d) => d.sync?.status === 'QUEUED').length
  return (
    <Button size="small" variant={waiting ? 'contained' : 'outlined'} disabled={syncing || !online}
      startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
      onClick={() => void sync(true)}>
      {waiting ? t('van.sendN', { n: waiting }) : t('van.sync')}
    </Button>
  )
}

function Home({ go }: { go: (v: View) => void }) {
  const { t, i18n } = useTranslation()
  const nav = useNavigate()
  const ar = i18n.language === 'ar'
  const { device, docs, online, syncError, locked, persistent, forget } = useVanState()
  const d = device!
  const b = d.bootstrap
  const today = riyadhDate()
  const mine = docs.filter((x) => x.doc_date === today)
  const day = daySummary(d, docs, today)
  const sold = day.sales
  const collected = day.collections
  const cash = day.cashToHand
  const waiting = docs.filter((x) => x.sync?.status === 'QUEUED').length
  const failed = docs.filter((x) => x.sync?.status === 'FAILED').length
  const age = daysOffline(d)
  const stale = age >= b.max_offline_days
  const [confirmForget, setConfirmForget] = useState(false)

  return (
    <Box>
      <TopBar title={
        <Box>
          <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
            {(ar && b.van.name_ar) || b.van.name_en}</Typography>
          <Typography sx={{ fontSize: 12, color: c.inkMuted }} noWrap>
            <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4,
              mr: 0.75, bgcolor: online ? 'var(--success-ink)' : 'var(--warning-ink)' }} />
            {online ? t('van.online') : t('van.offline')} · {b.van.salesman}
          </Typography>
        </Box>} right={<SyncButton />} />

      <Stack spacing={1.5} sx={{ p: 2 }}>
        {locked && <Alert severity="error">{t('van.locked', { why: locked })}</Alert>}
        {!persistent && <Alert severity="warning">{t('van.notPersistent')}</Alert>}
        {stale && <Alert severity="error">{t('van.tooOld', { days: age })}</Alert>}
        {!stale && age >= 1 && <Alert severity="info">{t('van.lastDownload', { days: age })}</Alert>}
        {failed > 0 && (
          <Alert severity="warning" action={<Button color="inherit" size="small"
            onClick={() => go({ name: 'docs' })}>{t('van.show')}</Button>}>
            {t('van.failedN', { n: failed })}</Alert>
        )}
        {syncError && online && !locked && <Alert severity="info">{t('van.syncLater', { why: syncError })}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <Stat label={t('van.soldToday')} value={fmtMoney(sold)} />
          <Stat label={t('van.cashInHand')} value={fmtMoney(cash)} strong />
          <Stat label={t('van.collected')} value={fmtMoney(collected)} />
          <Stat label={t('van.waiting')} value={String(waiting)}
            tone={waiting ? 'warn' : undefined} />
        </Box>

        <BigButton icon={<SellIcon />} label={t('van.newSale')} disabled={stale || !!locked}
          primary onClick={() => go({ name: 'pick', next: 'SALE' })} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <BigButton icon={<CollectIcon />} label={t('van.collect')} disabled={stale || !!locked}
            onClick={() => go({ name: 'pick', next: 'RECEIPT' })} />
          <BigButton icon={<ReturnIcon />} label={t('van.takeBack')} disabled={stale || !!locked}
            onClick={() => go({ name: 'pick', next: 'RETURN' })} />
        </Box>
        <Button variant="outlined" onClick={() => go({ name: 'docs' })}>
          {t('van.todaysDocs', { n: mine.length })}</Button>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <Button variant="outlined" startIcon={<DayIcon />} onClick={() => go({ name: 'day' })}>
            {t('van.endOfDay')}</Button>
          <Button variant="outlined" startIcon={<PrinterIcon />} onClick={() => go({ name: 'printer' })}>
            {t('van.printer')}</Button>
        </Box>

        <Typography sx={{ fontSize: 12, color: c.inkMuted, textAlign: 'center', pt: 1 }}>
          {b.series.SALE.prefix} · {t('van.phoneNameShort')}: {b.device.name}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button size="small" color="inherit" onClick={() => nav('/')}>{t('van.office')}</Button>
          <Button size="small" color="inherit" onClick={() => setConfirmForget(true)}>
            {t('van.removePhone')}</Button>
        </Stack>
      </Stack>

      <Dialog open={confirmForget} onClose={() => setConfirmForget(false)}>
        <DialogTitle>{t('van.removePhone')}</DialogTitle>
        <DialogContent>
          <Typography>{waiting || failed ? t('van.removeBlocked', { n: waiting + failed })
            : t('van.removeBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmForget(false)}>{t('common.close')}</Button>
          <Button color="error" disabled={waiting + failed > 0}
            onClick={() => { setConfirmForget(false); void forget() }}>{t('van.removeConfirm')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

function Stat({ label, value, strong, tone }: { label: string; value: string; strong?: boolean
                                                tone?: 'warn' }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: strong ? c.primary : c.surface,
      color: strong ? c.onPrimary : undefined, borderColor: strong ? 'transparent' : undefined }}>
      <Typography sx={{ fontSize: 12, opacity: 0.8 }}>{label}</Typography>
      <Typography className="num" sx={{ fontSize: 20, fontWeight: 600, textAlign: 'start',
        color: tone === 'warn' ? c.warningInk : undefined }}>{value}</Typography>
    </Paper>
  )
}

function BigButton({ icon, label, onClick, primary, disabled }: { icon: ReactNode; label: string
  onClick: () => void; primary?: boolean; disabled?: boolean }) {
  return (
    <ButtonBase onClick={onClick} disabled={disabled}
      sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-start', px: 2.5, py: primary ? 2.5 : 2,
            borderRadius: 2, border: `1px solid ${primary ? 'transparent' : c.border}`,
            bgcolor: primary ? c.primary : c.surface, color: primary ? c.onPrimary : c.ink,
            opacity: disabled ? 0.45 : 1, fontSize: primary ? 18 : 15, fontWeight: 600,
            '& svg': { fontSize: primary ? 28 : 22 } }}>
      {icon}<span>{label}</span>
    </ButtonBase>
  )
}

// --- customers ---------------------------------------------------------------

function PickCustomer({ kind, onBack, onPick }: { kind: DocKind; onBack: () => void
                                                  onPick: (code: string | null) => void }) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const { device } = useVanState()
  const [q, setQ] = useState('')
  const list = device!.bootstrap.customers
  const hits = useMemo(() => {
    const n = q.trim().toLowerCase()
    if (!n) return list
    return list.filter((x) => x.code.toLowerCase().includes(n) || x.name_en.toLowerCase().includes(n)
      || (x.name_ar ?? '').includes(q.trim()) || (x.phone ?? '').includes(n))
  }, [q, list])
  const title = kind === 'SALE' ? t('van.newSale') : kind === 'RETURN' ? t('van.takeBack')
    : t('van.collect')
  return (
    <Box>
      <TopBar title={<Typography sx={{ fontWeight: 600 }}>{title}</Typography>} onBack={onBack} />
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField fullWidth autoFocus placeholder={t('van.findCustomer')} value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
      </Box>
      <List sx={{ px: 1 }}>
        {kind !== 'RECEIPT' && (
          <ListItemButton onClick={() => onPick(null)} sx={{ borderRadius: 1.5, py: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>{t('inv.cashCustomer')}</Typography>
              <Typography sx={{ fontSize: 12.5, color: c.inkMuted }}>{t('van.walkInHint')}</Typography>
            </Box>
          </ListItemButton>
        )}
        {hits.map((x) => (
          <ListItemButton key={x.code} onClick={() => onPick(x.code)} sx={{ borderRadius: 1.5, py: 1.25 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap dir="auto" sx={{ fontWeight: 500 }}>{(ar && x.name_ar) || x.name_en}</Typography>
              <Typography sx={{ fontSize: 12.5, color: c.inkMuted }} noWrap>
                <span className="doc-no">{x.code}</span>{x.city ? ` · ${x.city}` : ''}
                {x.bills.length ? ` · ${t('van.nBills', { n: x.bills.length })}` : ''}</Typography>
            </Box>
            <Typography className="num" sx={{ fontWeight: 600,
              color: Number(x.balance) > 0 ? c.ink : c.inkMuted }}>{bal(x.balance)}</Typography>
          </ListItemButton>
        ))}
        {hits.length === 0 && <Typography sx={{ p: 2, color: c.inkMuted }}>{t('van.noCustomers')}</Typography>}
      </List>
    </Box>
  )
}

// --- selling and taking back -------------------------------------------------

interface Line extends DocLine { key: number }
let keys = 1

function Sell({ kind, customer, onDone }: { kind: 'SALE' | 'RETURN'; customer: string | null
                                            onDone: () => void }) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const { device, save } = useVanState()
  const b = device!.bootstrap
  const cust = customer ? b.customers.find((x) => x.code === customer) ?? null : null
  const [lines, setLines] = useState<Line[]>([])
  const [paid, setPaid] = useState(!cust)
  const [buyer, setBuyer] = useState('')
  const [buyerVat, setBuyerVat] = useState('')
  const [notes, setNotes] = useState('')
  const [q, setQ] = useState('')
  const [scan, setScan] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState<{ doc: VanDoc; print: boolean } | null>(null)
  const search = useRef<HTMLInputElement | null>(null)

  const item = (code: string) => b.items.find((i) => i.code === code)!
  const hits = useMemo(() => {
    const n = q.trim().toLowerCase()
    if (!n) return []
    return b.items.filter((o) => o.code.toLowerCase().includes(n) || o.name_en.toLowerCase().includes(n)
      || (o.name_ar ?? '').includes(q.trim()) || o.barcode === n
      || o.units.some((u) => u.barcode === n)).slice(0, 30)
  }, [q, b.items])

  function add(it: VanItem, unit?: string) {
    const u = unit ?? defaultUnit(it)
    setLines((ls) => {
      const same = ls.find((l) => l.item_code === it.code && l.unit === u)
      if (same) return ls.map((l) => (l === same ? { ...l, qty: String(Number(l.qty) + 1) } : l))
      return [...ls, { key: keys++, item_code: it.code, unit: u, qty: '1', price: listPrice(it, u) }]
    })
    setQ('')
    requestAnimationFrame(() => search.current?.focus())
  }

  function onCode(code: string) {
    const n = code.trim()
    const it = b.items.find((o) => o.barcode === n || o.units.some((u) => u.barcode === n)
      || o.code.toLowerCase() === n.toLowerCase())
    if (!it) { setError(t('van.unknownCode', { code: n })); return }
    setError(null)
    add(it, it.units.find((u) => u.barcode === n)?.unit)
  }

  const set = (key: number, patch: Partial<Line>) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  const sum = totals(b.items, lines)

  // What the van holds, item by item, against what this sale takes.
  const need = new Map<string, number>()
  for (const l of lines) {
    need.set(l.item_code, (need.get(l.item_code) ?? 0)
      + Number(l.qty || 0) * factorOf(item(l.item_code), l.unit))
  }
  const short = kind === 'SALE'
    ? [...need].filter(([code, q]) => q > Number(item(code).stock)).map(([code]) => code) : []
  const below = kind === 'SALE' ? lines.filter((l) => {
    const m = minimumFor(item(l.item_code), l.unit, l.qty)
    return m !== null && Number(priceLine(item(l.item_code), l).net) < Number(m)
  }).map((l) => l.item_code) : []

  const problems = [
    lines.length === 0 && t('inv.needLine'),
    lines.some((l) => !(Number(l.qty) > 0)) && t('inv.needQty'),
    lines.some((l) => l.price === '') && t('inv.needPrice'),
    short.length > 0 && !b.company.allow_negative_stock && t('van.notOnVan', { codes: short.join(', ') }),
    below.length > 0 && !b.may_override_price && t('inv.belowMinBlocked'),
    (paid || !cust) && !b.van.cash_code && t('van.noCash'),
  ].filter(Boolean) as string[]

  async function finish(print: boolean) {
    setSaving(true); setError(null)
    try {
      const doc = await save({
        kind, customer_code: cust?.code ?? null,
        customer_name: cust ? null : (buyer.trim() || null),
        customer_vat: cust ? null : (buyerVat.trim() || null),
        paid: paid || !cust,
        lines: lines.map(({ key: _k, ...l }) => l),
        subtotal: sum.subtotal, vat_amount: sum.vat, total: sum.total,
        notes: notes.trim() || null,
      })
      setSaved({ doc, print })
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  if (saved) return <Saved doc={saved.doc} print={saved.print} onDone={onDone} />

  return (
    <Box sx={{ pb: 22 }}>
      <TopBar onBack={onDone} title={
        <Box>
          <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap dir="auto">
            {kind === 'RETURN' ? `${t('van.takeBack')} · ` : ''}
            {cust ? ((ar && cust.name_ar) || cust.name_en) : t('inv.cashCustomer')}</Typography>
          {cust && <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
            {cust.code} · {t('van.balance')} {bal(cust.balance)}</Typography>}
        </Box>} />

      <Stack spacing={1.5} sx={{ p: 2 }}>
        {!cust && (
          <Stack direction="row" spacing={1}>
            <TextField size="small" label={t('inv.buyerName')} value={buyer} sx={{ flex: 1 }}
              onChange={(e) => setBuyer(e.target.value)} inputProps={{ maxLength: 160 }} />
            <TextField size="small" label={t('inv.buyerVat')} value={buyerVat} sx={{ flex: 1 }}
              onChange={(e) => setBuyerVat(e.target.value.trim())}
              inputProps={{ maxLength: 20, inputMode: 'numeric' }} />
          </Stack>
        )}
        <Stack direction="row" spacing={1}>
          <TextField fullWidth inputRef={search} placeholder={t('van.findItem')} value={q} autoFocus
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && q.trim()) { e.preventDefault(); onCode(q) } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
          {canScan() && (
            <IconButton aria-label={t('van.scan')} onClick={() => setScan(true)}
              sx={{ border: `1px solid ${c.border}`, borderRadius: 1.5, width: 56 }}><ScanIcon /></IconButton>
          )}
        </Stack>
        {hits.length > 0 && (
          <Paper variant="outlined">
            <List dense disablePadding>
              {hits.map((o) => (
                <ListItemButton key={o.id} onClick={() => add(o)} sx={{ py: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap dir="auto"><span className="doc-no">{o.code}</span> · {(ar && o.name_ar) || o.name_en}</Typography>
                    <Typography sx={{ fontSize: 12, color: Number(o.stock) > 0 ? c.inkMuted : c.dangerInk }}>
                      {t('van.onVan', { qty: fmtQty(o.stock), unit: o.base_unit })}
                      {o.sale_price ? ` · ${fmtMoney(listPrice(o, defaultUnit(o)))} / ${defaultUnit(o)}` : ''}</Typography>
                  </Box>
                </ListItemButton>
              ))}
            </List>
          </Paper>
        )}
        {error && <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>}

        {lines.map((l) => {
          const it = item(l.item_code)
          const p = priceLine(it, l)
          const min = minimumFor(it, l.unit, l.qty)
          const under = min !== null && Number(p.net) < Number(min)
          return (
            <Paper key={l.key} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography dir="auto" sx={{ fontWeight: 500 }}>{(ar && it.name_ar) || it.name_en}</Typography>
                  <Typography sx={{ fontSize: 12, color: short.includes(it.code) ? c.dangerInk : c.inkMuted }}>
                    {it.code} · {t('van.onVan', { qty: fmtQty(it.stock), unit: it.base_unit })}</Typography>
                </Box>
                <IconButton size="small" aria-label={t('inv.removeLine')}
                  onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}><DeleteIcon fontSize="small" /></IconButton>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <IconButton aria-label={t('van.less')} sx={{ border: `1px solid ${c.border}` }}
                  onClick={() => set(l.key, { qty: String(Math.max(0, Number(l.qty) - 1)) })}><RemoveIcon /></IconButton>
                <TextField size="small" value={l.qty} sx={{ width: 72 }}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => set(l.key, { qty: sanitize(e.target.value) })}
                  inputProps={{ inputMode: 'decimal', className: 'num', style: { textAlign: 'center' },
                    'aria-label': t('inv.qtyOnLine', { n: l.key }) }} />
                <IconButton aria-label={t('van.more')} sx={{ border: `1px solid ${c.border}` }}
                  onClick={() => set(l.key, { qty: String(Number(l.qty || 0) + 1) })}><AddIcon /></IconButton>
                <TextField select size="small" value={l.unit} sx={{ minWidth: 96, flex: 1 }}
                  onChange={(e) => set(l.key, { unit: e.target.value, price: listPrice(it, e.target.value) })}>
                  {unitsOf(it).map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
                <TextField size="small" label={t('inv.price')} value={l.price} sx={{ width: 120 }}
                  error={under && !b.may_override_price}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => set(l.key, { price: sanitize(e.target.value) })}
                  inputProps={{ inputMode: 'decimal', className: 'num', style: { textAlign: 'end' } }} />
                <Typography sx={{ flex: 1, fontSize: 12.5, lineHeight: 1.3,
                  color: under ? 'var(--warning-ink)' : 'var(--ink-muted)' }}>
                  {under ? t(b.may_override_price ? 'inv.belowMinOk' : 'inv.belowMin', { min: fmtMoney(min!) })
                    : `${t('inv.vat')} ${fmtMoney(p.vat)}`}</Typography>
                <Typography className="num" sx={{ fontWeight: 700 }}>{fmtMoney(p.total)}</Typography>
              </Stack>
            </Paper>
          )
        })}
        {lines.length > 0 && (
          <TextField size="small" label={t('inv.notes')} value={notes} placeholder={kind === 'RETURN'
            ? t('van.returnNotesHint') : undefined}
            onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 300 }} />
        )}
      </Stack>

      <Paper square elevation={8} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 3,
        px: 2, pt: 1.5, pb: 'calc(12px + env(safe-area-inset-bottom))', maxWidth: 560, mx: 'auto' }}>
        {cust && (
          <ToggleButtonGroup exclusive fullWidth size="small" value={paid ? 'cash' : 'credit'}
            onChange={(_, v) => v && setPaid(v === 'cash')} sx={{ mb: 1 }}>
            <ToggleButton value="credit">{kind === 'SALE' ? t('inv.onCredit') : t('van.creditAccount')}</ToggleButton>
            <ToggleButton value="cash">{kind === 'SALE' ? t('van.cashNow') : t('van.refundCash')}</ToggleButton>
          </ToggleButtonGroup>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography sx={{ fontSize: 12.5, color: c.inkMuted }}>
            {t('inv.vat')} {fmtMoney(sum.vat)} · {t('van.nLines', { n: lines.length })}</Typography>
          <Typography className="num" sx={{ fontSize: 24, fontWeight: 700 }}>SAR {fmtMoney(sum.total)}</Typography>
        </Stack>
        {problems.length > 0 && lines.length > 0 && (
          <Typography sx={{ fontSize: 12.5, color: c.warningInk }}>{problems[0]}</Typography>
        )}
        {short.length > 0 && b.company.allow_negative_stock && (
          <Typography sx={{ fontSize: 12.5, color: c.warningInk }}>{t('van.willGoNegative')}</Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button variant="outlined" disabled={problems.length > 0 || saving} onClick={() => void finish(false)}>
            {t('van.saveOnly')}</Button>
          <Button fullWidth variant="contained" size="large" startIcon={<PrintIcon />}
            disabled={problems.length > 0 || saving} onClick={() => void finish(true)}>
            {kind === 'SALE' ? t('van.saveAndPrint') : t('van.saveReturn')}</Button>
        </Stack>
      </Paper>

      {scan && <ScanDialog onClose={() => setScan(false)} onCode={(code) => { setScan(false); onCode(code) }} />}
    </Box>
  )
}

function Saved({ doc, print, onDone }: { doc: VanDoc; print?: boolean; onDone: () => void }) {
  const { t } = useTranslation()
  const { device, docs } = useVanState()
  const now = docs.find((d) => d.uid === doc.uid) ?? doc
  const [printing, setPrinting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const started = useRef(false)

  async function go() {
    setPrinting(true); setError(null)
    try {
      await printDoc(device!, doc)
    } catch (e) {
      setError(e instanceof Error && e.message === 'PAIR' ? t('van.printerPairAgain')
        : t('van.printFailed', { why: e instanceof Error ? e.message : String(e) }))
    } finally {
      setPrinting(false)
    }
  }
  useEffect(() => {
    if (print && !started.current) { started.current = true; void go() }
  }, [print])                     // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack spacing={2} sx={{ p: 3, textAlign: 'center' }}>
      <Typography sx={{ fontSize: 22, fontWeight: 600 }}>{t('van.saved')}</Typography>
      <Typography className="doc-no" sx={{ fontSize: 16 }}>{doc.doc_no}</Typography>
      <Typography className="num" sx={{ fontSize: 28, fontWeight: 700 }}>
        SAR {fmtMoney(doc.total ?? doc.amount ?? '0')}</Typography>
      <Box><StatusChip doc={now} /></Box>
      {error && <Alert severity="warning" sx={{ textAlign: 'start' }}>{error}</Alert>}
      <Button variant="outlined" size="large" disabled={printing}
        startIcon={printing ? <CircularProgress size={18} /> : <PrintIcon />}
        onClick={() => void go()}>{printing ? t('van.printing') : t('van.printAgain')}</Button>
      <Button variant="contained" size="large" autoFocus onClick={onDone}>{t('van.next')}</Button>
    </Stack>
  )
}

// --- collecting cash ---------------------------------------------------------

function Collect({ customer, onDone }: { customer: string; onDone: () => void }) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const { device, save } = useVanState()
  const b = device!.bootstrap
  const cust = b.customers.find((x) => x.code === customer) as VanCustomer
  const [amount, setAmount] = useState(isPositive(cust.balance) ? cust.balance : '')
  const [notes, setNotes] = useState('')
  const [saved, setSaved] = useState<VanDoc | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Oldest first: how the books will apply it.
  let left = Number(amount || 0)
  const applied = cust.bills.map((bill) => {
    const take = Math.max(0, Math.min(left, Number(bill.outstanding)))
    left -= take
    return { ...bill, take }
  })

  async function finish() {
    setSaving(true); setError(null)
    try {
      const doc = await save({ kind: 'RECEIPT', customer_code: cust.code, amount: Number(amount).toFixed(2),
                               total: Number(amount).toFixed(2), notes: notes.trim() || null })
      setSaved(doc)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }
  if (saved) return <Saved doc={saved} print onDone={onDone} />
  return (
    <Box>
      <TopBar onBack={onDone} title={
        <Box>
          <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap dir="auto">
            {t('van.collect')} · {(ar && cust.name_ar) || cust.name_en}</Typography>
          <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
            {cust.code} · {t('van.balance')} {bal(cust.balance)}</Typography>
        </Box>} />
      <Stack spacing={2} sx={{ p: 2 }}>
        <TextField label={t('van.amountReceived')} value={amount} autoFocus
          onChange={(e) => setAmount(sanitize(e.target.value))}
          inputProps={{ inputMode: 'decimal', className: 'num', style: { fontSize: 26, textAlign: 'end' } }}
          InputProps={{ startAdornment: <InputAdornment position="start">SAR</InputAdornment> }} />
        {cust.bills.length > 0 ? (
          <Paper variant="outlined">
            <Typography sx={{ px: 2, pt: 1.5, fontSize: 13, fontWeight: 600 }}>{t('van.openBills')}</Typography>
            {applied.map((bill) => (
              <Stack key={bill.ref} direction="row" justifyContent="space-between"
                sx={{ px: 2, py: 1, borderTop: `1px solid ${c.divider}` }}>
                <Box>
                  <Typography className="doc-no" sx={{ fontSize: 13 }}>{bill.ref}</Typography>
                  <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{bill.date}</Typography>
                </Box>
                <Box sx={{ textAlign: 'end' }}>
                  <Typography className="num" sx={{ fontSize: 13 }}>{fmtMoney(bill.outstanding)}</Typography>
                  {bill.take > 0 && <Typography className="num" sx={{ fontSize: 12, color: c.successInk }}>
                    −{fmtMoney(bill.take.toFixed(2))}</Typography>}
                </Box>
              </Stack>
            ))}
            {left > 0.004 && (
              <Typography sx={{ px: 2, py: 1, fontSize: 12.5, color: c.inkMuted, borderTop: `1px solid ${c.divider}` }}>
                {t('van.onAccount', { amount: fmtMoney(left.toFixed(2)) })}</Typography>
            )}
          </Paper>
        ) : (
          <Typography sx={{ color: c.inkMuted, fontSize: 13 }}>{t('van.noOpenBills')}</Typography>
        )}
        <TextField size="small" label={t('inv.notes')} value={notes} placeholder={t('van.collectNotesHint')}
          onChange={(e) => setNotes(e.target.value)} inputProps={{ maxLength: 300 }} />
        {error && <Alert severity="warning">{error}</Alert>}
        {!b.van.cash_code && <Alert severity="error">{t('van.noCash')}</Alert>}
        <Button variant="contained" size="large" startIcon={<PrintIcon />}
          disabled={!(Number(amount) > 0) || saving || !b.van.cash_code} onClick={() => void finish()}>
          {t('van.saveCollection')}</Button>
        <Typography sx={{ fontSize: 12, color: c.inkMuted }}>{t('van.collectNote')}</Typography>
      </Stack>
    </Box>
  )
}

// --- the day's documents -----------------------------------------------------

function StatusChip({ doc }: { doc: VanDoc }) {
  const { t } = useTranslation()
  const s = doc.sync?.status ?? 'QUEUED'
  const color = s === 'FAILED' ? 'error' : s === 'QUEUED' ? 'warning' : 'success'
  return <Chip size="small" color={color} variant="outlined" label={t(`van.status${s}`)} />
}

function Docs({ onBack }: { onBack: () => void }) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const { device, docs } = useVanState()
  const b = device!.bootstrap
  const [open, setOpen] = useState<VanDoc | null>(null)
  const name = (d: VanDoc) => {
    const cu = d.customer_code ? b.customers.find((x) => x.code === d.customer_code) : null
    return cu ? ((ar && cu.name_ar) || cu.name_en) : (d.customer_name || t('inv.cashCustomer'))
  }
  return (
    <Box>
      <TopBar onBack={onBack} title={<Typography sx={{ fontWeight: 600 }}>{t('van.documents')}</Typography>}
        right={<SyncButton />} />
      {docs.length === 0 ? <Typography sx={{ p: 3, color: c.inkMuted }}>{t('van.noDocs')}</Typography> : (
        <List>
          {docs.map((d) => (
            <ListItemButton key={d.uid} onClick={() => setOpen(d)} sx={{ py: 1.25 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap dir="auto" sx={{ fontWeight: 500 }}>{name(d)}</Typography>
                <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
                  <span className="doc-no">{d.doc_no}</span> · {new Date(d.issued_at).toLocaleTimeString(
                    i18n.language === 'ar' ? 'ar' : 'en-GB', { hour: '2-digit', minute: '2-digit' })}</Typography>
              </Box>
              <Stack alignItems="flex-end" spacing={0.5}>
                <Typography className="num" sx={{ fontWeight: 600 }}>
                  {d.kind === 'RETURN' ? '−' : ''}{fmtMoney(d.total ?? d.amount ?? '0')}</Typography>
                <StatusChip doc={d} />
              </Stack>
            </ListItemButton>
          ))}
        </List>
      )}
      <Dialog open={!!open} onClose={() => setOpen(null)} fullWidth>
        {open && <>
          <DialogTitle className="doc-no">{open.doc_no}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1 }}>{name(open)} · SAR {fmtMoney(open.total ?? open.amount ?? '0')}</Typography>
            <StatusChip doc={open} />
            {open.sync?.error && <Alert severity="error" sx={{ mt: 1.5 }}>{open.sync.error}</Alert>}
            {(open.sync?.warnings ?? []).map((w) => <Alert key={w} severity="warning" sx={{ mt: 1 }}>{w}</Alert>)}
            {open.sync?.status === 'FAILED' && (
              <Typography sx={{ mt: 1.5, fontSize: 13, color: c.inkMuted }}>{t('van.failedHelp')}</Typography>)}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(null)}>{t('common.close')}</Button>
            <Button variant="contained" startIcon={<PrintIcon />}
              onClick={() => void printDoc(device!, open).catch(() => undefined)}>{t('van.printAgain')}</Button>
          </DialogActions>
        </>}
      </Dialog>
    </Box>
  )
}

// --- the camera --------------------------------------------------------------

interface Detector { detect: (src: CanvasImageSource) => Promise<{ rawValue: string }[]> }
type DetectorCtor = new (o?: { formats?: string[] }) => Detector

function canScan(): boolean {
  return typeof window !== 'undefined' && 'BarcodeDetector' in window
    && !!navigator.mediaDevices?.getUserMedia
}

function ScanDialog({ onClose, onCode }: { onClose: () => void; onCode: (code: string) => void }) {
  const { t } = useTranslation()
  const video = useRef<HTMLVideoElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let stream: MediaStream | null = null
    let stop = false
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        if (!video.current) return
        video.current.srcObject = stream
        await video.current.play()
        const Ctor = (window as unknown as { BarcodeDetector: DetectorCtor }).BarcodeDetector
        const det = new Ctor({ formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code'] })
        while (!stop) {
          const found = await det.detect(video.current).catch(() => [])
          if (found.length) { onCode(found[0].rawValue); return }
          await new Promise((r) => setTimeout(r, 150))
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => { stop = true; stream?.getTracks().forEach((tr) => tr.stop()) }
  }, [onCode])
  return (
    <Dialog open onClose={onClose} fullWidth>
      <DialogTitle>{t('van.scan')}</DialogTitle>
      <DialogContent>
        {error ? <Alert severity="warning">{t('van.cameraFailed', { why: error })}</Alert> : (
          <Box component="video" ref={video} muted playsInline
            sx={{ width: '100%', borderRadius: 1, bgcolor: '#000', aspectRatio: '4 / 3' }} />
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t('common.close')}</Button></DialogActions>
    </Dialog>
  )
}

// --- the printer -------------------------------------------------------------

function PrinterScreen({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation()
  const { device } = useVanState()
  const [p, setP] = useState<PrinterSettings>(DEFAULT_PRINTER)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ sev: 'success' | 'warning'; text: string } | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  useEffect(() => { void loadPrinter().then(setP) }, [])
  const sample = useMemo(() => dayTicket(device!, daySummary(device!, [], riyadhDate())), [device])
  useEffect(() => {
    let gone = false
    void draw(sample, DOTS[p.paper]).then((cv) => { if (!gone) setPreview(cv.toDataURL()) })
    return () => { gone = true }
  }, [sample, p.paper])

  async function update(next: PrinterSettings) { setP(next); await savePrinter(next) }
  async function doPair() {
    setBusy(true); setMsg(null)
    try {
      const next = await pair(p)
      setP(next)
      setMsg({ sev: 'success', text: t('van.printerPaired', { name: next.name }) })
    } catch (e) {
      setMsg({ sev: 'warning', text: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }
  async function test() {
    setBusy(true); setMsg(null)
    try {
      await printBlocks(sample, p)
      setMsg({ sev: 'success', text: t('van.testSent') })
    } catch (e) {
      setMsg({ sev: 'warning', text: e instanceof Error && e.message === 'PAIR'
        ? t('van.printerPairAgain') : t('van.printFailed', { why: e instanceof Error ? e.message : String(e) }) })
    } finally {
      setBusy(false)
    }
  }
  const bt = bluetoothAvailable()
  return (
    <Box>
      <TopBar onBack={onBack} title={<Typography sx={{ fontWeight: 600 }}>{t('van.printer')}</Typography>} />
      <Stack spacing={2} sx={{ p: 2 }}>
        <ToggleButtonGroup exclusive fullWidth value={p.mode}
          onChange={(_, v) => v && void update({ ...p, mode: v })}>
          <ToggleButton value="bluetooth" disabled={!bt}>{t('van.bluetooth')}</ToggleButton>
          <ToggleButton value="dialog">{t('van.printDialog')}</ToggleButton>
        </ToggleButtonGroup>
        {!bt && <Alert severity="info">{t('van.noBluetooth')}</Alert>}
        {p.mode === 'bluetooth' && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 13, color: c.inkMuted }}>{t('van.pairedPrinter')}</Typography>
            <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{p.name ?? t('van.nonePaired')}</Typography>
            <Button variant="outlined" disabled={busy} onClick={() => void doPair()}>
              {p.id ? t('van.pairAnother') : t('van.pair')}</Button>
            <Typography sx={{ fontSize: 12.5, color: c.inkMuted, mt: 1.5 }}>{t('van.pairHint')}</Typography>
          </Paper>
        )}
        <TextField select label={t('van.paper')} value={p.paper}
          onChange={(e) => void update({ ...p, paper: Number(e.target.value) as 80 | 58 })}>
          <MenuItem value={80}><span dir="ltr">80 mm</span></MenuItem>
          <MenuItem value={58}><span dir="ltr">58 mm</span></MenuItem>
        </TextField>
        {msg && <Alert severity={msg.sev}>{msg.text}</Alert>}
        <Button variant="contained" size="large" startIcon={<PrintIcon />} disabled={busy
          || (p.mode === 'bluetooth' && !p.id)} onClick={() => void test()}>{t('van.testPrint')}</Button>
        {preview && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 12, color: c.inkMuted, mb: 1 }}>{t('van.previewNote')}</Typography>
            <Box component="img" src={preview} alt="" sx={{ width: '100%', maxWidth: 320, bgcolor: '#fff',
              border: `1px solid ${c.border}`, borderRadius: 1, imageRendering: 'pixelated' }} />
          </Box>
        )}
      </Stack>
    </Box>
  )
}

// --- the end of the day ------------------------------------------------------

function EndOfDay({ onBack }: { onBack: () => void }) {
  const { t, i18n } = useTranslation()
  const ar = i18n.language === 'ar'
  const { device, docs, online, sync, applyDownload } = useVanState()
  const d = device!
  const date = riyadhDate()
  const s = daySummary(d, docs, date)
  const waiting = docs.filter((x) => x.sync?.status === 'QUEUED').length
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ sev: 'success' | 'warning' | 'error'; text: string } | null>(null)
  const [confirm, setConfirm] = useState(false)

  async function printDay() {
    setMsg(null)
    try {
      await printBlocks(dayTicket(d, s), await loadPrinter())
    } catch (e) {
      try { await printViaDialog(dayTicket(d, s), (await loadPrinter()).paper) } catch { /* nothing */ }
      setMsg({ sev: 'warning', text: t('van.printFailed', { why: e instanceof Error ? e.message : String(e) }) })
    }
  }
  async function unload() {
    setConfirm(false); setBusy(true); setMsg(null)
    try {
      await sync()
      if ((await loadDocs()).some((x) => x.sync?.status === 'QUEUED')) {
        throw new Error(t('van.syncFirst'))
      }
      const r = await unloadVan(d.token)
      await applyDownload(r.bootstrap)
      setMsg({ sev: 'success', text: t('van.unloaded', { doc: r.doc_no, store: r.to_store, n: r.lines }) })
    } catch (e) {
      setMsg({ sev: 'error', text: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }
  const row = (label: string, value: string, strong?: boolean) => (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75,
      borderBottom: `1px solid ${c.divider}`, fontWeight: strong ? 700 : 400, fontSize: strong ? 18 : 15 }}>
      <span>{label}</span><span className="num">{value}</span>
    </Stack>
  )
  const stockLeft = d.bootstrap.items.filter((i) => Number(i.stock) > 0)
  return (
    <Box>
      <TopBar onBack={onBack} title={<Typography sx={{ fontWeight: 600 }}>{t('van.endOfDay')}</Typography>}
        right={<SyncButton />} />
      <Stack spacing={2} sx={{ p: 2 }}>
        {waiting > 0 && <Alert severity="warning">{t('van.waitingToSend', { n: waiting })}</Alert>}
        <Paper variant="outlined" sx={{ p: 2 }}>
          {row(t('van.invoicesN', { n: s.invoices }), fmtMoney(s.sales))}
          {row(`· ${t('van.cashNow')}`, fmtMoney(s.cashSales))}
          {row(`· ${t('inv.onCredit')}`, fmtMoney(s.creditSales))}
          {row(t('van.returnsToday'), fmtMoney(s.returns))}
          {row(t('van.collectionsN', { n: s.collectionsCount }), fmtMoney(s.collections))}
          {row(t('van.cashRefunds'), fmtMoney(s.refunds))}
          {row(t('van.cashToHand'), `SAR ${fmtMoney(s.cashToHand)}`, true)}
        </Paper>
        <Button variant="outlined" size="large" startIcon={<PrintIcon />} onClick={() => void printDay()}>
          {t('van.printDay')}</Button>

        <Paper variant="outlined">
          <Typography sx={{ px: 2, pt: 1.5, fontWeight: 600 }}>{t('van.stockLeft')}</Typography>
          {stockLeft.length === 0 ? (
            <Typography sx={{ px: 2, py: 1.5, color: c.inkMuted }}>{t('van.vanEmpty')}</Typography>
          ) : stockLeft.map((i) => (
            <Stack key={i.code} direction="row" justifyContent="space-between"
              sx={{ px: 2, py: 1, borderTop: `1px solid ${c.divider}` }}>
              <Typography dir="auto" sx={{ fontSize: 14 }} noWrap>{(ar && i.name_ar) || i.name_en}</Typography>
              <Typography className="num" sx={{ fontSize: 14 }}>{fmtQty(i.stock)} {i.base_unit}</Typography>
            </Stack>
          ))}
        </Paper>
        {msg && <Alert severity={msg.sev}>{msg.text}</Alert>}
        <Button variant="contained" size="large" disabled={busy || !online || stockLeft.length === 0}
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}
          onClick={() => setConfirm(true)}>{t('van.unload')}</Button>
        <Typography sx={{ fontSize: 12.5, color: c.inkMuted }}>
          {online ? t('van.unloadHint') : t('van.unloadOffline')}</Typography>
      </Stack>
      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle>{t('van.unload')}</DialogTitle>
        <DialogContent><Typography>{t('van.unloadBody', { n: stockLeft.length })}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>{t('common.close')}</Button>
          <Button variant="contained" onClick={() => void unload()}>{t('van.unload')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
